import { QueryClient, QueryFunction } from "@tanstack/react-query";
import { apiUrl, credentialsMode, getToken } from "@/lib/net";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    let jsonError;
    try {
      jsonError = await res.json();
    } catch (parseError) {
      // If JSON parsing fails, create a basic error
      throw new Error(`${res.status}: ${res.statusText}`);
    }

    // Create and throw error with proper message and all fields
    // API returns either 'error' or 'message' field, check both
    const error = new Error(jsonError.message || jsonError.error || res.statusText);
    (error as any).requiresApproval = jsonError.requiresApproval;
    (error as any).redirectTo = jsonError.redirectTo;
    (error as any).details = jsonError.details; // Preserve validation details
    (error as any).error = jsonError.error; // Preserve error type
    (error as any).status = res.status;
    (error as any).body = jsonError; // Full parsed error body (e.g. 409 mc_confirmation_pending carries mcName/requestedAt)
    throw error;
  }
}

export async function apiRequest(
  url: string,
  options?: {
    method?: string;
    data?: unknown;
  }
): Promise<Response> {
  const method = options?.method || "GET";
  const data = options?.data;
  const token = getToken();
  const user = localStorage.getItem("user");
  const headers: Record<string, string> = data ? { "Content-Type": "application/json" } : {};
  
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  // Add user ID header for session-based authentication
  if (user) {
    try {
      const userData = JSON.parse(user);
      if (userData.id) {
        headers['x-user-id'] = userData.id.toString();
      }
    } catch (e) {
      // Ignore JSON parse errors
    }
  }

  const res = await fetch(apiUrl(url), {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: credentialsMode(),
  });

  await throwIfResNotOk(res);
  return res;
}

/**
 * Auth-aware fetch wrapper. Injects the same Bearer token + x-user-id header +
 * credentials as `apiRequest`/`getQueryFn`, but returns the raw Response so it
 * can be used for FormData uploads and custom queryFns that call `.json()`
 * themselves. Use this instead of a bare `fetch()` for any authenticated API —
 * a bare fetch sends no auth and will 401 against gated mounts (e.g. /api/venue).
 */
export async function authFetch(input: string, init: RequestInit = {}): Promise<Response> {
  const token = getToken();
  const user = localStorage.getItem("user");
  const headers = new Headers(init.headers || {});

  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }
  if (user) {
    try {
      const userData = JSON.parse(user);
      if (userData.id) {
        headers.set("x-user-id", userData.id.toString());
      }
    } catch (e) {
      // Ignore JSON parse errors
    }
  }

  // credentialsMode() is FORCED (overrides init.credentials): in the app a credentialed
  // cross-origin request fails because appCors never sends Allow-Credentials.
  return fetch(apiUrl(input), { ...init, headers, credentials: credentialsMode() });
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const token = getToken();
    const user = localStorage.getItem("user");
    const headers: Record<string, string> = {};
    
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
    
    // Add user ID header for session-based authentication
    if (user) {
      try {
        const userData = JSON.parse(user);
        if (userData.id) {
          headers['x-user-id'] = userData.id.toString();
        }
      } catch (e) {
        // Ignore JSON parse errors
      }
    }

    const res = await fetch(apiUrl(queryKey[0] as string), {
      credentials: credentialsMode(),
      headers,
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      // Some callers opt in to silent null on 401 (e.g., optional-auth endpoints).
      // For those we don't redirect — caller knows what it's doing.
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes — mutations invalidate immediately
      gcTime: 5 * 60 * 1000, // 5 minutes
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});
